/**
 * The Decision Room's own-matter weighing and its strategy board.
 *
 * Two things the chief executive brings to the room that the platform's
 * records do not put there:
 *
 *  - **A matter of their own.** Anything — a hire, a supplier, a project, a
 *    policy. They name the choices, say what matters and how much, and give
 *    each choice a score or the numbers behind it (cost, benefit, months,
 *    risk). The room adds it up the same way every time: a weighted score out
 *    of 100, the margin between the first two, and the weight at which the
 *    leader would change. Nothing is guessed — a number that was not given is
 *    a gap, and the room says so.
 *
 *  - **The board.** Every decision, the platform's and theirs, as a piece on a
 *    four-by-four board: when it is dealt with (the files, a–d) against how
 *    much it matters (the ranks, 4–1). Moving a piece re-scores the whole
 *    position: a decision parked after its own decide-by is in check, more
 *    critical work in "Now" than the room can take at once is overload, and
 *    cash committed past the budget entered is overspend. The score is
 *    arithmetic on those three, stated with its reasons, never a feel.
 *
 * Deterministic and free of the screen, so the tests can hold it to account.
 */

import { t } from "../i18n";

/* ── own matters ──────────────────────────────────────────────────────── */

/** Where a criterion's score comes from: typed by the executive, or worked out from an option's numbers. */
export type CriterionSource = "score" | "cost" | "benefit" | "months" | "risk";

export interface MatterOption {
  key: string;
  name: string;
  /** cash out, in the site currency */
  cost?: number | null;
  /** cash or value in, in the site currency */
  benefit?: number | null;
  /** how long it takes */
  months?: number | null;
  /** 1 low · 2 medium · 3 high */
  risk?: 1 | 2 | 3 | null;
  note?: string;
}

export interface Criterion {
  key: string;
  name: string;
  /** how much it matters, 0 (not at all) … 5 (above everything) */
  weight: number;
  source: CriterionSource;
}

export interface DiscussionEntry {
  id: string;
  at: string;
  by: string;
  text: string;
  /** who wrote it: the executive, the room's own arithmetic, or a model */
  from: "you" | "room" | "model" | "withheld";
}

export interface Matter {
  id: string;
  title: string;
  question: string;
  category: string;
  /** yyyy-mm-dd */
  decideBy?: string;
  /** how much it matters on the board: critical, high, medium, low */
  impact: BoardRowKey;
  createdBy: string;
  createdAt: string;
  options: MatterOption[];
  criteria: Criterion[];
  /** option key → criterion key → 0…10, for criteria scored by hand */
  scores: Record<string, Record<string, number | undefined>>;
  discussion: DiscussionEntry[];
  decided?: { option: string; rationale: string; at: string; by: string };
}

export interface CriterionScore {
  key: string;
  name: string;
  weight: number;
  /** 0…10, or null when it was not given */
  score: number | null;
  /** worked out from the numbers rather than typed */
  derived: boolean;
}

export interface OptionResult {
  key: string;
  name: string;
  /** 0…100 */
  total: number;
  rank: number;
  parts: CriterionScore[];
  missing: number;
}

export interface WeightFlip {
  criterion: string;
  now: number;
  at: number;
  leader: string;
}

export interface MatterEvaluation {
  rows: OptionResult[];
  leader: OptionResult | null;
  runnerUp: OptionResult | null;
  /** points between the first two */
  margin: number | null;
  /** "clear", "narrow" (under 5 points) or "tie" */
  call: "clear" | "narrow" | "tie" | "none";
  flips: WeightFlip[];
  gaps: string[];
  /** points per 100,000 of cost, where a cost was given */
  value: { key: string; name: string; perHundredK: number }[];
  figures: Set<string>;
}

const r1 = (n: number) => Math.round(n * 10) / 10;
const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

export const SOURCE_LABEL: Record<CriterionSource, string> = {
  score: "your score, 0–10",
  cost: "from the cost — cheaper scores higher",
  benefit: "from the benefit — larger scores higher",
  months: "from the time — quicker scores higher",
  risk: "from the risk — lower scores higher",
};

/**
 * A criterion worked out from an option's numbers, scaled across the options
 * so the best scores 10 and the worst 0. When every option has the same
 * number, each scores 10: none is worse. An option without the number is a
 * gap, not a zero someone chose.
 */
function derivedScores(options: MatterOption[], source: Exclude<CriterionSource, "score">): (number | null)[] {
  const value = (o: MatterOption): number | null => {
    const v = o[source];
    return v == null || !Number.isFinite(Number(v)) ? null : Number(v);
  };
  const vals = options.map(value);
  const known = vals.filter((v): v is number => v != null);
  if (!known.length) return vals.map(() => null);
  const lo = Math.min(...known);
  const hi = Math.max(...known);
  const higherBetter = source === "benefit";
  return vals.map((v) => {
    if (v == null) return null;
    if (hi === lo) return 10;
    const frac = higherBetter ? (v - lo) / (hi - lo) : (hi - v) / (hi - lo);
    return r1(10 * frac);
  });
}

/** Every option's score on every criterion, before any weighting. */
function scoreTable(m: Matter): (number | null)[][] {
  const byCriterion = m.criteria.map((c) => {
    if (c.source === "score") {
      return m.options.map((o) => {
        const v = m.scores[o.key]?.[c.key];
        return v == null || !Number.isFinite(v) ? null : clamp(Number(v), 0, 10);
      });
    }
    return derivedScores(m.options, c.source);
  });
  /* transposed: option → criterion */
  return m.options.map((_, i) => byCriterion.map((col) => col[i]));
}

function totals(table: (number | null)[][], weights: number[]): number[] {
  const max = weights.reduce((a, w) => a + w * 10, 0);
  return table.map((row) => {
    if (!max) return 0;
    const got = row.reduce<number>((a, s, j) => a + (s ?? 0) * weights[j], 0);
    return r1((100 * got) / max);
  });
}

const leaderOf = (xs: number[]): number => {
  let best = -1;
  xs.forEach((v, i) => {
    if (best < 0 || v > xs[best]) best = i;
  });
  return best;
};

export function evaluateMatter(m: Matter): MatterEvaluation {
  const figures = new Set<string>();
  const empty: MatterEvaluation = {
    rows: [],
    leader: null,
    runnerUp: null,
    margin: null,
    call: "none",
    flips: [],
    gaps: [],
    value: [],
    figures,
  };
  if (!m.options.length) return { ...empty, gaps: [t("No choices named yet.")] };
  const table = scoreTable(m);
  const weights = m.criteria.map((c) => clamp(Number(c.weight) || 0, 0, 5));
  const tot = totals(table, weights);
  const order = tot.map((_, i) => i).sort((a, b) => tot[b] - tot[a] || a - b);
  const rows: OptionResult[] = m.options.map((o, i) => ({
    key: o.key,
    name: o.name,
    total: tot[i],
    rank: order.indexOf(i) + 1,
    parts: m.criteria.map((c, j) => ({
      key: c.key,
      name: c.name,
      weight: weights[j],
      score: table[i][j],
      derived: c.source !== "score",
    })),
    missing: table[i].filter((s) => s == null).length,
  }));
  rows.forEach((r) => figures.add(String(r.total)));

  const gaps: string[] = [];
  if (!m.criteria.length) gaps.push(t("No criteria yet — say what matters, and how much."));
  if (m.criteria.length && weights.every((w) => w === 0)) gaps.push(t("Every criterion is weighted 0, so nothing counts yet."));
  rows.forEach((r) =>
    r.parts.forEach((p) => {
      if (p.score == null)
        gaps.push(
          p.derived
            ? t("{option}: no figure for “{criterion}” yet, so it counts as 0.", { option: r.name, criterion: p.name })
            : t("{option}: “{criterion}” is not scored yet, so it counts as 0.", { option: r.name, criterion: p.name }),
        );
    }),
  );

  const sorted = [...rows].sort((a, b) => a.rank - b.rank);
  const leader = m.criteria.length && weights.some((w) => w > 0) ? sorted[0] : null;
  const runnerUp = leader && sorted.length > 1 ? sorted[1] : null;
  const margin = leader && runnerUp ? r1(leader.total - runnerUp.total) : null;
  if (margin != null) figures.add(String(margin));
  const call: MatterEvaluation["call"] = !leader
    ? "none"
    : margin == null
      ? "clear"
      : margin === 0
        ? "tie"
        : margin < 5
          ? "narrow"
          : "clear";

  /*
   * What would change the answer: each criterion's weight swept 0…5 in half
   * steps with the others held, and the nearest weight at which another
   * option leads. The weight is the thing the executive set, so it is the
   * thing worth knowing the edge of.
   */
  const flips: WeightFlip[] = [];
  if (leader) {
    const li = m.options.findIndex((o) => o.key === leader.key);
    m.criteria.forEach((c, j) => {
      let best: WeightFlip | null = null;
      for (let w2 = 0; w2 <= 5; w2 += 0.5) {
        if (w2 === weights[j]) continue;
        const ws = weights.map((w, k) => (k === j ? w2 : w));
        const t2 = totals(table, ws);
        const l2 = leaderOf(t2);
        if (l2 !== li && t2[l2] > t2[li]) {
          if (!best || Math.abs(w2 - weights[j]) < Math.abs(best.at - weights[j]))
            best = { criterion: c.name, now: weights[j], at: w2, leader: m.options[l2].name };
        }
      }
      if (best) {
        flips.push(best);
        figures.add(String(best.at));
      }
    });
    flips.sort((a, b) => Math.abs(a.at - a.now) - Math.abs(b.at - b.now));
  }

  const value = rows
    .map((r) => {
      const cost = m.options.find((o) => o.key === r.key)?.cost;
      return cost && cost > 0 ? { key: r.key, name: r.name, perHundredK: r1((r.total / cost) * 100_000) } : null;
    })
    .filter((v): v is { key: string; name: string; perHundredK: number } => v != null)
    .sort((a, b) => b.perHundredK - a.perHundredK);
  value.forEach((v) => figures.add(String(v.perHundredK)));

  return { rows, leader, runnerUp, margin, call, flips, gaps, value, figures };
}

/**
 * The room's own answer to a question about a matter, from the arithmetic
 * alone; null when the question is not one it can answer.
 */
export function answerMatter(q: string, _m: Matter, e: MatterEvaluation): string | null {
  const s = q.toLowerCase();
  if (!e.leader) return e.gaps[0] ?? t("There is nothing to weigh yet: name the choices and what matters.");
  const lead = e.leader;
  if (/(best|lead|recommend|which|winner|choose|pick|top)/.test(s)) {
    const second = e.runnerUp ? " " + t("{option} is next on {score}.", { option: e.runnerUp.name, score: e.runnerUp.total }) : "";
    const how =
      e.call === "tie"
        ? " " + t("It is a tie — the scores do not decide this; judgment does.")
        : e.call === "narrow"
          ? " " + t("The margin is narrow, so small changes in weight or score could reverse it.")
          : "";
    return t("{option} leads on {score} out of 100.", { option: lead.name, score: lead.total }) + second + how;
  }
  if (/(change|flip|swing|sensitiv|what if|depend)/.test(s)) {
    if (!e.flips.length) return t("No single weight between 0 and 5 changes the leader: {option} holds.", { option: lead.name });
    return e.flips.slice(0, 3).map(flipSentence).join(" ");
  }
  if (/(against|weak|risk|downside|worst)/.test(s)) {
    const weakest = [...lead.parts].filter((p) => p.weight > 0).sort((a, b) => (a.score ?? 0) - (b.score ?? 0))[0];
    return weakest
      ? t("{option} is weakest on “{criterion}”: {score} of 10, weighted {weight}.", {
          option: lead.name,
          criterion: weakest.name,
          score: weakest.score ?? 0,
          weight: weakest.weight,
        })
      : t("{option} has no weighted criterion to be weak on.", { option: lead.name });
  }
  if (/(cost|money|value|price|cheap|afford)/.test(s)) {
    if (!e.value.length) return t("No costs were entered, so value for money cannot be worked out.");
    return e.value.map((v) => t("{option}: {points} points per 100,000 spent.", { option: v.name, points: v.perHundredK })).join(" ");
  }
  if (/(missing|gap|unknown|not scored)/.test(s)) {
    return e.gaps.length ? e.gaps.slice(0, 4).join(" ") : t("Every choice is scored on every criterion.");
  }
  return null;
}

/** "If X were weighted 4 instead of 2, Y would lead." */
export const flipSentence = (f: WeightFlip) =>
  t("If “{criterion}” were weighted {at} instead of {now}, {option} would lead.", {
    criterion: f.criterion,
    at: f.at,
    now: f.now,
    option: f.leader,
  });

/** What a model is given about a matter: the numbers the room produced, and nothing it did not. */
export function materialForMatter(m: Matter, e: MatterEvaluation, currency: string): string {
  const lines = [
    `Matter: ${m.title}`,
    `Question: ${m.question || m.title}`,
    m.decideBy ? `Decide by: ${m.decideBy}` : "",
    `Criteria (weight 0-5): ${m.criteria.map((c) => `${c.name} ${c.weight}`).join("; ") || "none"}`,
    ...m.options.map((o) => {
      const r = e.rows.find((x) => x.key === o.key);
      const nums = [
        o.cost != null ? `cost ${o.cost} ${currency}` : "",
        o.benefit != null ? `benefit ${o.benefit} ${currency}` : "",
        o.months != null ? `${o.months} months` : "",
        o.risk != null ? `risk ${["", "low", "medium", "high"][o.risk]}` : "",
      ]
        .filter(Boolean)
        .join(", ");
      return `Option ${o.name}: score ${r?.total ?? "-"} of 100${nums ? ` (${nums})` : ""}${o.note ? ` — ${o.note}` : ""}`;
    }),
    e.leader ? `Leader: ${e.leader.name}, margin ${e.margin ?? "-"}` : "No leader yet",
    ...e.flips.map((f) => `Flip: ${f.criterion} at weight ${f.at} makes ${f.leader} lead`),
    ...m.discussion.filter((d) => d.from === "you").slice(-5).map((d) => `Executive's note: ${d.text}`),
  ];
  return lines.filter(Boolean).join("\n");
}

/** Starting points, so a blank page is never the only way in. */
export interface MatterTemplate {
  key: string;
  label: string;
  what: string;
  options: string[];
  criteria: { name: string; weight: number; source: CriterionSource }[];
}

export const MATTER_TEMPLATES: MatterTemplate[] = [
  {
    key: "project",
    label: "Invest in a project",
    what: "Go ahead now, go ahead smaller, or wait.",
    options: ["Go ahead in full", "A smaller first phase", "Wait a year"],
    criteria: [
      { name: "Cost", weight: 3, source: "cost" },
      { name: "Benefit", weight: 4, source: "benefit" },
      { name: "Time to deliver", weight: 2, source: "months" },
      { name: "Risk", weight: 3, source: "risk" },
      { name: "Fits the strategy", weight: 3, source: "score" },
    ],
  },
  {
    key: "supplier",
    label: "Choose a supplier or contractor",
    what: "Compare bidders on price, quality and track record.",
    options: ["Bidder A", "Bidder B", "Bidder C"],
    criteria: [
      { name: "Price", weight: 4, source: "cost" },
      { name: "Quality of the offer", weight: 4, source: "score" },
      { name: "Track record", weight: 3, source: "score" },
      { name: "Time to mobilise", weight: 2, source: "months" },
    ],
  },
  {
    key: "people",
    label: "Hire, outsource or reorganise",
    what: "Build the team in-house, buy the service, or share it.",
    options: ["Hire in-house", "Outsource", "A mixed model"],
    criteria: [
      { name: "Yearly cost", weight: 4, source: "cost" },
      { name: "Control and quality", weight: 3, source: "score" },
      { name: "Speed to start", weight: 2, source: "months" },
      { name: "Risk", weight: 3, source: "risk" },
    ],
  },
  {
    key: "policy",
    label: "A policy or a position to take",
    what: "Weigh the options on the things only you can judge.",
    options: ["Option 1", "Option 2"],
    criteria: [
      { name: "Safety", weight: 5, source: "score" },
      { name: "Passengers", weight: 3, source: "score" },
      { name: "Reputation", weight: 3, source: "score" },
      { name: "Cost", weight: 2, source: "cost" },
    ],
  },
  { key: "blank", label: "Start from a blank page", what: "Your own choices and your own criteria.", options: ["Option 1", "Option 2"], criteria: [] },
];

let seq = 0;
export const newId = (prefix: string) => `${prefix}-${Date.now().toString(36)}${(++seq).toString(36)}`;

export function matterFromTemplate(tpl: MatterTemplate, title: string, by: string, now = new Date()): Matter {
  const options = tpl.options.map((name, i) => ({ key: `o${i + 1}`, name }));
  const criteria = tpl.criteria.map((c, i) => ({ key: `c${i + 1}`, ...c }));
  return {
    id: newId("matter"),
    title: title.trim() || tpl.label,
    question: "",
    category: tpl.label,
    impact: "high",
    createdBy: by,
    createdAt: now.toISOString(),
    options,
    criteria,
    scores: {},
    discussion: [],
  };
}

/* ── the strategy board ───────────────────────────────────────────────── */

export type BoardColKey = "now" | "quarter" | "year" | "later";
export type BoardRowKey = "critical" | "high" | "medium" | "low";

/** The files: when a decision is dealt with. `days` is the last day the file covers. */
export const BOARD_COLS: { key: BoardColKey; file: string; label: string; days: number }[] = [
  { key: "now", file: "a", label: "Now", days: 14 },
  { key: "quarter", file: "b", label: "This quarter", days: 90 },
  { key: "year", file: "c", label: "This year", days: 365 },
  { key: "later", file: "d", label: "Later", days: Infinity },
];

/** The ranks, top to bottom: how much it matters. */
export const BOARD_ROWS: { key: BoardRowKey; rank: number; label: string }[] = [
  { key: "critical", rank: 4, label: "Critical" },
  { key: "high", rank: 3, label: "High" },
  { key: "medium", rank: 2, label: "Medium" },
  { key: "low", rank: 1, label: "Low" },
];

/** A decision as a piece. */
export interface Piece {
  id: string;
  title: string;
  kind: "desk" | "matter";
  /** the platform decision's frame, or "matter" */
  frame: string;
  /** cash out if taken as recommended (or as leading), where known */
  cost: number | null;
  /** days until it must be decided; null when there is no date */
  decideByDays: number | null;
  oneWay: boolean;
  /** 0…1: regulatory exposure left if it is not dealt with */
  exposure: number;
  /** where it belongs on its merits: the rank the room would give it */
  impact: BoardRowKey;
}

export interface Square {
  col: number;
  row: number;
}

export type Placement = Record<string, Square>;

export interface BoardSettings {
  /** cash that may be committed in "Now" and "This quarter" together; null = not set */
  budget: number | null;
  /** how many critical or high pieces "Now" can take at once */
  focus: number;
}

export const DEFAULT_BOARD_SETTINGS: BoardSettings = { budget: null, focus: 3 };

/** The chess name of a square: file a–d and rank 4–1. */
export const squareName = (sq: Square) => `${BOARD_COLS[sq.col]?.file ?? "?"}${BOARD_ROWS[sq.row]?.rank ?? "?"}`;

const colFor = (days: number | null) => {
  if (days == null) return 2;
  const i = BOARD_COLS.findIndex((c) => days <= c.days);
  return i < 0 ? BOARD_COLS.length - 1 : i;
};

/** The room's opening position: each piece by its decide-by and on its merits. */
export function suggestedPlacement(pieces: Piece[]): Placement {
  const out: Placement = {};
  for (const p of pieces) {
    out[p.id] = { col: colFor(p.decideByDays), row: BOARD_ROWS.findIndex((r) => r.key === p.impact) };
  }
  return out;
}

/**
 * The rank a platform decision deserves. What it is about comes first: a
 * runway below its limit and the regulator's inspection are critical whatever
 * the recommended fix costs — ranking them by the price of the cure put a
 * crossed friction limit in rank 1. Then the door and the exposure, then the
 * money.
 */
export function impactForDecision(frame: string, oneWay: boolean, exposure: number, cost: number | null): BoardRowKey {
  if (frame === "runway" || frame === "regulator") return "critical";
  if (frame === "peak") return oneWay ? "critical" : "high";
  return impactFor(oneWay, exposure, cost);
}

/** The rank by consequence alone: hard to undo or regulator-exposed is critical. */
export function impactFor(oneWay: boolean, exposure: number, cost: number | null): BoardRowKey {
  if (oneWay || exposure >= 0.5) return "critical";
  if (exposure > 0 || (cost ?? 0) >= 1_000_000) return "high";
  if ((cost ?? 0) >= 100_000) return "medium";
  return "low";
}

export type CheckKind = "late" | "overload" | "budget" | "buried" | "clutter";

export interface Check {
  kind: CheckKind;
  pieceId?: string;
  text: string;
  /** points taken off the position */
  cost: number;
}

export interface BoardEvaluation {
  score: number;
  grade: "Strong position" | "Playable" | "Under pressure" | "Losing position";
  checks: Check[];
  /** cash committed in each file */
  cash: number[];
  /** pieces per square, row-major */
  counts: number[][];
  committed: number;
  /** critical and high pieces in "Now" */
  focusUsed: number;
}

/** the highest a position can score while a critical piece is past its decide-by: "Under pressure" */
export const CRITICAL_LATE_CAP = 55;

const GRADE = (n: number): BoardEvaluation["grade"] =>
  n >= 80 ? "Strong position" : n >= 60 ? "Playable" : n >= 40 ? "Under pressure" : "Losing position";

/**
 * The position, scored out of 100. Every point taken off is a check with its
 * reason, so the number can always be read back:
 *
 *  - **late** — a piece in a file that ends after its own decide-by: 15 off
 *    for a critical piece, 8 for the rest. The deadline does not move because
 *    the piece did.
 *  - **buried** — a critical piece put down in rank 1 or 2: 10 off. What
 *    matters most has been ranked as if it did not.
 *  - **overload** — more critical and high pieces in "Now" than the focus the
 *    executive set: 6 off for each one over.
 *  - **budget** — cash committed in "Now" and "This quarter" beyond the
 *    budget entered: 5 off for every 10 % over, at most 30.
 *  - **clutter** — low pieces taking "Now": 3 off each; attention spent on
 *    small things first.
 *
 * And one ceiling: while a critical piece is past its decide-by the position
 * scores at most 55, "Under pressure", whatever else is clean.
 */
export function evaluateBoard(pieces: Piece[], placement: Placement, settings: BoardSettings): BoardEvaluation {
  const checks: Check[] = [];
  const cash = BOARD_COLS.map(() => 0);
  const counts = BOARD_ROWS.map(() => BOARD_COLS.map(() => 0));
  let focusUsed = 0;
  for (const p of pieces) {
    const sq = placement[p.id];
    if (!sq) continue;
    counts[sq.row][sq.col] += 1;
    if (p.cost) cash[sq.col] += p.cost;
    const col = BOARD_COLS[sq.col];
    const rowKey = BOARD_ROWS[sq.row].key;
    const critical = p.impact === "critical";
    if (p.decideByDays != null && sq.col > 0) {
      /* the file starts the day after the previous one ends */
      const starts = BOARD_COLS[sq.col - 1].days + 1;
      if (starts > p.decideByDays) {
        const late = starts - Math.max(p.decideByDays, 0);
        checks.push({
          kind: "late",
          pieceId: p.id,
          text: t("{title} is due in {days} days, but “{file}” starts at day {starts} — at least {late} days late.", {
            title: p.title,
            days: p.decideByDays,
            file: t(col.label),
            starts,
            late,
          }),
          cost: critical ? 15 : 8,
        });
      }
    }
    if (critical && (rowKey === "medium" || rowKey === "low")) {
      checks.push({
        kind: "buried",
        pieceId: p.id,
        text: t("{title} matters most, but sits in rank {rank} ({level}).", {
          title: p.title,
          rank: BOARD_ROWS[sq.row].rank,
          level: t(BOARD_ROWS[sq.row].label),
        }),
        cost: 10,
      });
    }
    if (sq.col === 0 && (rowKey === "critical" || rowKey === "high")) focusUsed += 1;
    if (sq.col === 0 && rowKey === "low") {
      checks.push({
        kind: "clutter",
        pieceId: p.id,
        text: t("{title} is low but takes a place in “Now”.", { title: p.title }),
        cost: 3,
      });
    }
  }
  const focus = Math.max(1, Math.round(settings.focus || 1));
  if (focusUsed > focus) {
    checks.push({
      kind: "overload",
      text: t("{n} critical or high matters in “Now” against a focus of {focus}.", { n: focusUsed, focus }),
      cost: 6 * (focusUsed - focus),
    });
  }
  const committed = cash[0] + cash[1];
  if (settings.budget != null && settings.budget >= 0 && committed > settings.budget) {
    const over = settings.budget > 0 ? (committed - settings.budget) / settings.budget : 1;
    checks.push({
      kind: "budget",
      text: t("{committed} committed in “Now” and “This quarter” against a budget of {budget} — {pct}% over.", {
        committed: Math.round(committed).toLocaleString("en"),
        budget: Math.round(settings.budget).toLocaleString("en"),
        pct: Math.round(over * 100),
      }),
      cost: Math.min(30, 5 * Math.ceil(over * 10)),
    });
  }
  /* a critical decision past its own deadline is never a strong position, however clean the rest */
  const criticalLate = checks.some((k) => k.kind === "late" && pieces.find((p) => p.id === k.pieceId)?.impact === "critical");
  const raw = clamp(100 - checks.reduce((a, c) => a + c.cost, 0), 0, 100);
  const score = criticalLate ? Math.min(raw, CRITICAL_LATE_CAP) : raw;
  return { score, grade: GRADE(score), checks, cash, counts, committed, focusUsed };
}

export interface Move {
  pieceId: string;
  title: string;
  from: Square;
  to: Square;
  before: number;
  after: number;
}

/** The move in the room's notation, and what it did to the position. */
export function describeMove(m: Move): string {
  const d = m.after - m.before;
  const change = d === 0 ? t("no change") : d > 0 ? `+${d}` : `${d}`;
  return `${m.title}: ${squareName(m.from)} → ${squareName(m.to)} (${m.before} → ${m.after}, ${change})`;
}

/** The best square for one piece, holding the others where they are — the room's suggested reply. */
export function bestSquare(
  pieces: Piece[],
  placement: Placement,
  settings: BoardSettings,
  pieceId: string,
): { square: Square; score: number } | null {
  if (!placement[pieceId]) return null;
  let best: { square: Square; score: number } | null = null;
  for (let row = 0; row < BOARD_ROWS.length; row++) {
    for (let col = 0; col < BOARD_COLS.length; col++) {
      const score = evaluateBoard(pieces, { ...placement, [pieceId]: { col, row } }, settings).score;
      const cur = placement[pieceId];
      const here = cur.col === col && cur.row === row;
      /* ties go to the square it is on, then to the earlier file — never a pointless move */
      if (!best || score > best.score || (score === best.score && here)) best = { square: { col, row }, score };
    }
  }
  return best;
}
